"use client";

import { useCallback, useState } from "react";
import { Eye, Loader2 } from "lucide-react";
import { useSDKActionWithDeps } from "@/@sdk/sdk.hooks";
import { Button } from "@/components/ui/button";
import { BaseModal } from "@/components/ui/base-modal";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { BrandEmailTemplateSendTestButton } from "./brand-email-template-send-test-button";
import { cn } from "@/lib/utils";

export type BrandEmailTemplatePreviewType =
  | "booking-confirmation"
  | "booking-confirmation-amended"
  | "booking-confirmation-voluntary-cancellation"
  | "booking-confirmation-involuntary-cancellation"
  | "booking-confirmation-refund"
  | "booking-confirmation-passenger-change"
  | "payment-reminder"
  | "voucher"
  | "customer-receipt";

type PreviewPayload = {
  subjectline: string;
  body: string;
};

const PREVIEW_TITLES: Record<BrandEmailTemplatePreviewType, string> = {
  "booking-confirmation": "Booking confirmation email",
  "booking-confirmation-amended": "Amended booking confirmation email",
  "booking-confirmation-voluntary-cancellation":
    "Voluntary cancellation confirmation email",
  "booking-confirmation-involuntary-cancellation":
    "Involuntary cancellation confirmation email",
  "booking-confirmation-refund": "Refund confirmation email",
  "booking-confirmation-passenger-change": "Passenger change confirmation email",
  "payment-reminder": "Payment reminder email",
  voucher: "Voucher email",
  "customer-receipt": "Customer receipt email",
};

const BOOKING_CONFIRMATION_TABS: {
  type: BrandEmailTemplatePreviewType;
  label: string;
}[] = [
  { type: "booking-confirmation", label: "Booking Confirmation" },
  {
    type: "booking-confirmation-voluntary-cancellation",
    label: "Voluntary Cancellation",
  },
  {
    type: "booking-confirmation-involuntary-cancellation",
    label: "Involuntary Cancellation",
  },
  { type: "booking-confirmation-refund", label: "Refund Confirmation" },
  { type: "booking-confirmation-amended", label: "Date / Product Change" },
  {
    type: "booking-confirmation-passenger-change",
    label: "Passenger Change",
  },
];

function isBookingConfirmationFamily(
  templateType: BrandEmailTemplatePreviewType
): boolean {
  return templateType.startsWith("booking-confirmation");
}

function sanitizeHtmlForSrcDoc(html: string): string {
  return html.replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, "");
}

interface BrandEmailTemplatePreviewButtonProps {
  brandId: string;
  templateType: BrandEmailTemplatePreviewType;
  title?: string;
  disabled?: boolean;
  variant?: "ghost" | "outline";
  size?: "sm" | "default";
}

export function BrandEmailTemplatePreviewButton({
  brandId,
  templateType,
  title,
  disabled,
  variant = "ghost",
  size = "sm",
}: BrandEmailTemplatePreviewButtonProps) {
  const showVariantTabs = isBookingConfirmationFamily(templateType);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [activeType, setActiveType] =
    useState<BrandEmailTemplatePreviewType>(templateType);
  const [previewCache, setPreviewCache] = useState<
    Partial<Record<BrandEmailTemplatePreviewType, PreviewPayload>>
  >({});
  const [loadingType, setLoadingType] =
    useState<BrandEmailTemplatePreviewType | null>(null);

  const previewTitle = title ?? PREVIEW_TITLES[templateType];
  const activePreview = previewCache[activeType] ?? null;

  const { doAction: fetchPreview } = useSDKActionWithDeps(
    () => ({
      action: (sdk) => (type: BrandEmailTemplatePreviewType) =>
        sdk.brands.getEmailTemplatePreview(brandId, type),
      failureMessage: "Failed to load email preview",
    }),
    [brandId]
  );

  const loadPreviewType = useCallback(
    async (type: BrandEmailTemplatePreviewType, openModal: boolean) => {
      setLoadingType(type);
      try {
        const data = await fetchPreview(type);
        if (!data) return;
        setPreviewCache((prev) => ({ ...prev, [type]: data }));
        setActiveType(type);
        if (openModal) {
          setPreviewOpen(true);
        }
      } finally {
        setLoadingType(null);
      }
    },
    [fetchPreview]
  );

  const handleOpenPreview = () => {
    setPreviewCache({});
    void loadPreviewType(templateType, true);
  };

  const handleTabChange = (nextType: string) => {
    const type = nextType as BrandEmailTemplatePreviewType;
    setActiveType(type);
    if (previewCache[type]) return;
    void loadPreviewType(type, false);
  };

  const handleOpenChange = (open: boolean) => {
    setPreviewOpen(open);
    if (!open) {
      setPreviewCache({});
      setActiveType(templateType);
      setLoadingType(null);
    }
  };

  const isOpening = loadingType === templateType && !previewOpen;

  return (
    <>
      <Button
        type="button"
        variant={variant}
        size={size}
        disabled={disabled || isOpening}
        onClick={handleOpenPreview}
      >
        {isOpening ? (
          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
        ) : (
          <Eye className="mr-2 h-4 w-4" />
        )}
        Preview email
      </Button>

      <BaseModal
        title={previewTitle}
        description={
          activePreview?.subjectline
            ? `Subject: ${activePreview.subjectline}`
            : "Sample email with your current saved brand configuration."
        }
        open={previewOpen}
        onOpenChange={handleOpenChange}
        footer={
          <div className="flex justify-end">
            <BrandEmailTemplateSendTestButton
              brandId={brandId}
              templateType={activeType}
              variant="outline"
            />
          </div>
        }
        dialogContentClassName="max-w-4xl"
      >
        {showVariantTabs ? (
          <Tabs
            value={activeType}
            onValueChange={handleTabChange}
            className="mb-3"
          >
            <div className="overflow-x-auto">
              <TabsList className="inline-flex h-auto min-w-full w-max flex-nowrap justify-start gap-1 bg-muted p-1">
                {BOOKING_CONFIRMATION_TABS.map((tab) => (
                  <TabsTrigger
                    key={tab.type}
                    value={tab.type}
                    className={cn(
                      "shrink-0 px-2.5 py-1.5 text-xs sm:text-sm whitespace-nowrap"
                    )}
                  >
                    {tab.label}
                  </TabsTrigger>
                ))}
              </TabsList>
            </div>
          </Tabs>
        ) : null}

        {loadingType === activeType && !activePreview ? (
          <div className="flex h-[60vh] items-center justify-center rounded-md border">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : activePreview?.body ? (
          <div className="h-[60vh] overflow-hidden rounded-md border">
            <iframe
              title="Email preview"
              className="h-full w-full border-0 bg-white"
              srcDoc={sanitizeHtmlForSrcDoc(activePreview.body)}
            />
          </div>
        ) : null}
      </BaseModal>
    </>
  );
}
